import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, MessageCircle, MapPin, Clock, 
  Send, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DemoRequestForm } from '../components/DemoRequestForm';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      value: 'contactus@sozialsetu.com',
      link: 'mailto:contactus@sozialsetu.com',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '+91 99865 73456',
      link: 'https://wa.me/919986573456',
      highlight: true,
    },
    {
      icon: Clock,
      title: 'Response Time',
      value: 'Within 2 hours',
      link: null,
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Mumbai, India',
      link: null,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setIsSuccess(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="section-spacing relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-2 mb-6">
              <MessageCircle className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">We're Here to Help</span>
            </div>

            <h1 className="text-hero text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get in <span className="text-cyan-400">Touch</span>
            </h1>

            <p className="text-body max-w-2xl mx-auto">
              Have questions? Want a demo? Our team is ready to help you protect your digital identity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-12" data-testid="contact-info">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-2xl text-center ${
                  info.highlight 
                    ? 'bg-green-600/20 border border-green-500/30' 
                    : 'bg-slate-900/50 border border-slate-800'
                }`}
              >
                <info.icon className={`w-8 h-8 mx-auto mb-3 ${
                  info.highlight ? 'text-green-400' : 'text-cyan-400'
                }`} />
                <h3 className="text-white font-bold mb-1">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className={`text-sm hover:underline ${
                      info.highlight ? 'text-green-400' : 'text-slate-400 hover:text-cyan-400'
                    }`}
                    data-testid={`contact-link-${info.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-sm text-slate-400">{info.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="section-spacing bg-slate-900/30" data-testid="forms-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Demo Request Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Request a Demo
              </h2>
              <p className="text-slate-400 mb-8">
                See how Sozialsetu protects creators like you.
              </p>
              <DemoRequestForm />
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Send Us a Message
              </h2>
              <p className="text-slate-400 mb-8">
                Questions, feedback, or just want to say hi.
              </p>

              {isSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Message Sent!
                  </h3>
                  <p className="text-slate-400">
                    We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="text-slate-300">Your Name *</Label>
                    <Input
                      id="contact-name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={handleChange}
                      className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-slate-300">Email *</Label>
                    <Input
                      id="contact-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={contactForm.email}
                      onChange={handleChange}
                      className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
                      data-testid="contact-form-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-subject" className="text-slate-300">Subject</Label>
                    <Input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      placeholder="What's this about?"
                      value={contactForm.subject}
                      onChange={handleChange}
                      className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
                      data-testid="contact-form-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-slate-300">Message *</Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      placeholder="Tell us more..."
                      value={contactForm.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl text-white placeholder:text-slate-500 resize-none"
                      data-testid="contact-form-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full h-14 font-bold text-base transition-all hover:scale-[1.02]"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="section-spacing bg-slate-950 relative" data-testid="whatsapp-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <MessageCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Prefer <span className="text-green-400">WhatsApp</span>?
            </h2>
            <p className="text-body max-w-2xl mx-auto mb-8">
              Get instant answers. Chat with our team directly on WhatsApp.
            </p>
            <a
              href="https://wa.me/919986573456"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105"
              data-testid="whatsapp-main-cta"
            >
              <MessageCircle className="w-6 h-6" />
              Chat on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
